/**
 * @fileOverview This file contains the implementation of the Toolbar component used in the questionnaire module.
 * @version 1.0.0
 * @since 1.0.0
 * @author Miguel Ángel Bermea Rodríguez
 */

import React, { useRef, useState } from 'react'
import "../../scss/Components/Questionnaire/_toolbar.scss"
import { Typography, ButtonGroup, IconButton, Menu, MenuItem, Button } from '@mui/material'
import { useTranslationHook } from '../../hooks/UseTranslationHook';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCaretDown } from '@fortawesome/pro-regular-svg-icons';

/**
 * Actions toolbar component for questionnaire module
 * @param {State} {isButtonDisabled} - State related to multiple elimination functionality
 * @param {State} {rowSelectionModel} - State related to multiple elimination functionality
 * @param {State} {sectionName} - State related to name of selected section
 * @param {Function} {setModalQuestionShow} - Function to change state of modalQuestionShow
 * @param {Function} {setModalMessageShow} - Function to change state of modalMessageShow
 * @param {Function} {setModalSectionShow} - Function to change state of modalSectionShow
 * @param {Function} {setModalQMultipleDeleteShow} - Function to change state of modalQMultipleDeleteShow
 * @returns {Component} Toolbar component
 */
const Toolbar = ({ 
    isButtonDisabled, 
    rowSelectionModel, 
    sectionName,
    setModalQuestionShow,
    setModalMessageShow,
    setModalSectionShow,
    setModalQMultipleDeleteShow
}) => {
    const {
        qQuestionsMultDeleteString,
        qQuestionsAddQString,
        qQuestionsAddMString,
        qQuestionsAddTitleString,
    } = useTranslationHook();

    /**
     * Manages the selection of multiple questions for deletion
     */
    function manageQuestionMultipleDelete() {
        localStorage.setItem("qMultipleDeleteList", rowSelectionModel);
    }; 

    //#region Main Btn Group

    const [openMainBtnOptions, setOpenMainBtnOptions] = useState(false);
    const mainActionRef = useRef(null);
    const mainAnchorRef = useRef(null);
    const [mainSelectedIndex, setMainSelectedIndex] = useState(0);

    const mainBtnOptions = [
        qQuestionsAddQString,
        qQuestionsAddMString,
        qQuestionsAddTitleString,
    ];

    /**
     * Handles the click event for the "Add Question" button
     */
    const handleAddQBtn = () => {
        setModalQuestionShow(true)
    };

    /**
     * Handles the click event for the "Add Message" button
     */
    const handleAddMBtn = () => {
        setModalMessageShow(true)
    };

    /**
     * Handles the click event for the "Add Section" button
     */
    const handleAddSectionBtn = () => {
        setModalSectionShow(true)
    };

    const mainBtnActionsMap = [
        handleAddQBtn,
        handleAddMBtn,
        handleAddSectionBtn,
    ];

    /**
     * Handles the click event for the main menu item
     * @param {Event} event - The click event
     * @param {number} index - The index of the selected main menu item
     */
    const handleMainMenuItemClick = (event, index) => {
        setMainSelectedIndex(index);
        setOpenMainBtnOptions(false);
    };

    //#endregion

    return (
        <div className="toolbar-component-container">
            <div className="section-name-container">
                <Typography className='section-name'>{sectionName}</Typography>
            </div>
            
            <div className="action-btns-container">
                <ButtonGroup ref={mainAnchorRef} aria-label="split button" className="main-group-btn">
                    <Button onClick={mainBtnActionsMap[mainSelectedIndex]} className='main-action-btn'>
                        {mainBtnOptions[mainSelectedIndex]}
                    </Button>
                    <IconButton
                        className="main-group-icon-btn"
                        aria-controls={openMainBtnOptions ? 'split-button-menu' : undefined}
                        aria-expanded={openMainBtnOptions ? 'true' : undefined}
                        aria-label="select merge strategy"
                        aria-haspopup="menu"
                        onMouseDown={() => {
                            mainActionRef.current = () => setOpenMainBtnOptions(!openMainBtnOptions);
                        }}
                        onKeyDown={() => {
                            mainActionRef.current = () => setOpenMainBtnOptions(!openMainBtnOptions);
                        }}
                        onClick={() => {
                            mainActionRef.current?.();
                        }}
                    >
                        <FontAwesomeIcon icon={faCircleCaretDown}/>
                    </IconButton>
                </ButtonGroup>
                <Menu
                    open={openMainBtnOptions}
                    onClose={() => setOpenMainBtnOptions(false)}
                    anchorEl={mainAnchorRef.current}
                >
                    {mainBtnOptions.map((option, index) => (
                        <MenuItem
                            key={option}
                            selected={index === mainSelectedIndex}
                            onClick={(event) => handleMainMenuItemClick(event, index)}
                        >
                            {option}
                        </MenuItem>
                    ))}
                </Menu>

                <Button
                    className="mult-deleteq-btn"
                    disabled={isButtonDisabled}
                    onClick={() => { setModalQMultipleDeleteShow(true); manageQuestionMultipleDelete() }}
                >
                    {qQuestionsMultDeleteString}
                </Button>
            </div>
        </div>
    )
}

export default Toolbar
